import * as Cesium from 'cesium'
import type { CdDiffCategory, CdDiffVisible } from '../../types/changeDetection'

export interface CdActiveDiff {
  diffId: number
  name: string
  added: number
  removed: number
  changed: number
  /** 실제 결과 3D Tiles URL 목록 (2단계). 비어 있으면 합성 미리보기 렌더 */
  realTilesets: string[]
  /** 이 diff 가 비교한 관측 id 목록 — 합성 미리보기를 실제 데이터 위치에 앵커링하는 데 사용 */
  obsIds: number[]
  /** 추가/제거/변경 복셀의 지도 표시 여부 */
  visible: CdDiffVisible
}

/** 합성 diff 엔티티에 심어두는 카테고리 태그 (표시/숨김 토글용) */
interface TaggedEntity extends Cesium.Entity {
  _cdCat?: CdDiffCategory
}

export const KOREA = Cesium.Cartographic.fromDegrees(127.8, 37.5, 0)

export function authToken(): string | undefined {
  try {
    const s = localStorage.getItem('auth')
    return s ? JSON.parse(s).token : undefined
  } catch {
    return undefined
  }
}

function enuPoint(origin: Cesium.Cartographic, e: number, n: number, u: number): Cesium.Cartesian3 {
  const cart = Cesium.Cartesian3.fromRadians(origin.longitude, origin.latitude, origin.height)
  const frame = Cesium.Transforms.eastNorthUpToFixedFrame(cart)
  return Cesium.Matrix4.multiplyByPoint(frame, new Cesium.Cartesian3(e, n, u), new Cesium.Cartesian3())
}

/** 관측 데이터 위에 뿌리는 반투명 voxel 격자(합성 미리보기) */
export function buildVoxelCloud(viewer: Cesium.Viewer, origin: Cesium.Cartographic): Cesium.Entity[] {
  const ents: Cesium.Entity[] = []
  const size = 4
  const nx = 10
  const ny = 10
  const nz = 6
  let seed = 1337
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296
    return seed / 4294967296
  }
  for (let ix = 0; ix < nx; ix++) {
    for (let iy = 0; iy < ny; iy++) {
      for (let iz = 0; iz < nz; iz++) {
        if (rand() > 0.42) continue
        const e = (ix - nx / 2) * size
        const n = (iy - ny / 2) * size
        const u = iz * size + size / 2
        const hue = 0.58 - 0.13 * (iz / nz)
        const color = Cesium.Color.fromHsl(hue, 0.75, 0.55, 0.35)
        ents.push(
          viewer.entities.add({
            position: enuPoint(origin, e, n, u),
            box: {
              dimensions: new Cesium.Cartesian3(size * 0.9, size * 0.9, size * 0.9),
              material: color,
              outline: true,
              outlineColor: color.withAlpha(0.7),
            },
          }),
        )
      }
    }
  }
  return ents
}

/** 변화탐지 결과(추가/제거/변화) 합성 시각화. 각 엔티티에 카테고리를 태깅하고
 *  `diff.visible` 에 따라 초기 표시 여부를 정한다 (이후 토글은 applyDiffCloudVisibility). */
export function buildDiffCloud(
  viewer: Cesium.Viewer,
  origin: Cesium.Cartographic,
  diff: CdActiveDiff,
): Cesium.Entity[] {
  const ents: Cesium.Entity[] = []
  const size = 3
  const clusters: { cat: CdDiffCategory; vol: number; color: Cesium.Color; cx: number; cy: number }[] = [
    { cat: 'add', vol: diff.added, color: Cesium.Color.fromCssColorString('#22c55e').withAlpha(0.55), cx: -16, cy: 10 },
    { cat: 'rem', vol: diff.removed, color: Cesium.Color.fromCssColorString('#ef4444').withAlpha(0.55), cx: 16, cy: 10 },
    { cat: 'chg', vol: diff.changed, color: Cesium.Color.fromCssColorString('#f59e0b').withAlpha(0.55), cx: 0, cy: -16 },
  ]
  let seed = 991
  const rand = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648
    return seed / 2147483648
  }
  for (const c of clusters) {
    const count = Math.max(3, Math.min(140, Math.round((c.vol || 0) / 5)))
    for (let i = 0; i < count; i++) {
      const e = c.cx + (rand() - 0.5) * 14
      const n = c.cy + (rand() - 0.5) * 14
      const u = (i % 8) * size + size / 2
      const ent: TaggedEntity = viewer.entities.add({
        show: diff.visible[c.cat],
        position: enuPoint(origin, e, n, u),
        box: {
          dimensions: new Cesium.Cartesian3(size * 0.9, size * 0.9, size * 0.9),
          material: c.color,
          outline: true,
          outlineColor: c.color.withAlpha(0.9),
        },
      })
      ent._cdCat = c.cat
      ents.push(ent)
    }
  }
  return ents
}

/** 이미 만들어진 합성 diff 엔티티들의 표시 여부를 카테고리별로 갱신한다. */
export function applyDiffCloudVisibility(ents: Cesium.Entity[], visible: CdDiffVisible): void {
  for (const ent of ents as TaggedEntity[]) {
    const cat = ent._cdCat
    if (cat) ent.show = visible[cat]
  }
}

/** 실제 voxel 3D Tiles 를 반투명하게 표시해 아래쪽 원본(포인트 등)이 비쳐 보이게 한다. */
export function applyVoxelOpacity(ts: Cesium.Cesium3DTileset, opacity: number): void {
  if (!ts || ts.isDestroyed()) return
  ts.customShader = new Cesium.CustomShader({
    translucencyMode: Cesium.CustomShaderTranslucencyMode.TRANSLUCENT,
    fragmentShaderText: `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
  material.alpha = ${Math.min(1, Math.max(0.05, opacity)).toFixed(2)};
}`,
  })
}

/** 포인트 클라우드 원본 타일의 점 크기(px). 메쉬 타일에는 영향이 없다. */
export function applyPointSize(ts: Cesium.Cesium3DTileset, size: number): void {
  if (!ts || ts.isDestroyed()) return
  ts.style = new Cesium.Cesium3DTileStyle({ pointSize: String(size) })
}

/**
 * 실제 결과 3D Tiles(2단계)의 카테고리별 표시 여부·색을 CustomShader 로 제어한다.
 * voxelizer diff 산출물은 복셀 색을 구워 넣는다(참고: voxel-based-change-detection):
 *   추가 = 적색 채널 우세, 제거 = 청색 채널 우세, 변경 = 3채널 모두 높음(회백).
 * 구운 색이 범례(추가=초록/제거=빨강/변경=주황)와 달라 혼동되므로, 살아남은 프래그먼트를
 * 범례 색으로 다시 칠하고 숨긴 카테고리는 discard 한다.
 */
export function applyDiffTilesetVisibility(ts: Cesium.Cesium3DTileset, visible: CdDiffVisible): void {
  if (!ts || ts.isDestroyed()) return
  const dropChg = !visible.chg ? 'if (isChg) { discard; }' : ''
  const dropAdd = !visible.add ? 'if (isAdd) { discard; }' : ''
  const dropRem = !visible.rem ? 'if (isRem) { discard; }' : ''
  ts.colorBlendMode = Cesium.Cesium3DTileColorBlendMode.REPLACE
  ts.customShader = new Cesium.CustomShader({
    lightingModel: Cesium.LightingModel.PBR,
    translucencyMode: Cesium.CustomShaderTranslucencyMode.TRANSLUCENT,
    fragmentShaderText: `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
  bool isChg = material.diffuse.r > 0.5 && material.diffuse.g > 0.5 && material.diffuse.b > 0.5;
  bool isAdd = material.diffuse.r > 0.7 && !isChg;
  bool isRem = material.diffuse.b > 0.7 && !isChg;

  ${dropChg}
  ${dropAdd}
  ${dropRem}

  // 범례 색으로 재도색: 추가=에메랄드(#22c55e), 제거=레드(#ef4444), 변경=앰버(#f59e0b)
  if (isAdd) {
    material.diffuse  = vec3(0.13, 0.77, 0.37);
    material.emissive = vec3(0.10, 0.55, 0.27);
  } else if (isRem) {
    material.diffuse  = vec3(0.94, 0.27, 0.27);
    material.emissive = vec3(0.65, 0.15, 0.15);
  } else if (isChg) {
    material.diffuse  = vec3(0.96, 0.62, 0.04);
    material.emissive = vec3(0.62, 0.40, 0.02);
  }
  material.alpha = 0.85;
}`,
  })
}
